import React, { useState, useEffect, useRef, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { Button, Card, Row, Col, Form, Badge, Alert, Spinner, Modal } from 'react-bootstrap';
import { LoadingState } from '../../components/common/LoadingState';
import { useTranslation } from 'react-i18next';
import Lightbox from "yet-another-react-lightbox";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import axiosInstance from '../../api/axios';

function Gallery() {
  const { t } = useTranslation();
  const { isDark } = useContext(ThemeContext) || {};
  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'b2c', 'b2b', 'reserva', 'unassigned', 'assigned'
  const [productFilter, setProductFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletingSrc, setDeletingSrc] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);

  // Modal de asignación a producto
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTargetPhoto, setAssignTargetPhoto] = useState(null);
  const [assignProductId, setAssignProductId] = useState('');
  const [assignRole, setAssignRole] = useState('b2c');
  const [assigning, setAssigning] = useState(false);
  const [productSearchModal, setProductSearchModal] = useState('');

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/catalog/gallery');
      if (response.data.success) {
        setPhotos(response.data.data.map((img) => ({
          ...img,
          originalSrc: img.src,
        })));
      }
    } catch (err) {
      console.error('Error cargando galería:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get('/admin/catalog?per_page=1000');
      if (response.data.success) {
        setProducts(response.data.data || []);
      }
    } catch (err) {
      console.error('Error cargando lista de productos para filtro:', err);
    }
  };

  useEffect(() => {
    void fetchGallery();
    void fetchProducts();
  }, []);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setErrorMessage('');
    setSuccessMessage('');
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('images[]', files[i]);
    }

    try {
      const response = await axiosInstance.post('/admin/catalog/gallery/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setSuccessMessage(response.data.message || t('gallery_images_uploaded', 'Imágenes subidas correctamente.'));
        await fetchGallery();
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(err.response?.data?.message || t('error_uploading_images', 'Error subiendo imágenes al servidor.'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (src, e) => {
    e.stopPropagation();
    if (!window.confirm(t('confirm_delete_image_server', '¿Seguro que deseas eliminar permanentemente esta imagen del servidor?'))) return;
    
    setDeletingSrc(src);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const response = await axiosInstance.delete('/admin/catalog/gallery/delete', {
        data: { src: src }
      });
      if (response.data.success) {
        setSuccessMessage(response.data.message || t('gallery_image_deleted', 'Imagen eliminada exitosamente.'));
        await fetchGallery();
      }
    } catch (err) {
      const msg = err.response?.data?.message || t('error_deleting_image', 'Error eliminando la imagen.');
      setErrorMessage(msg);
    } finally {
      setDeletingSrc(null);
    }
  };

  // Abrir modal de asignación
  const handleOpenAssignModal = (photo, defaultRole = 'b2c') => {
    setAssignTargetPhoto(photo);
    setAssignRole(defaultRole);
    setAssignProductId(photo.assigned_to?.id || (products.length > 0 ? products[0].id : ''));
    setProductSearchModal('');
    setShowAssignModal(true);
  };

  // Enviar asignación al backend
  const handleAssignSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!assignTargetPhoto || !assignProductId) {
      alert('Por favor selecciona un producto válido.');
      return;
    }
    setAssigning(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const response = await axiosInstance.post('/admin/catalog/gallery/assign', {
        image_url: assignTargetPhoto.originalSrc || assignTargetPhoto.src,
        product_id: assignProductId,
        rol: assignRole
      });
      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setShowAssignModal(false);
        setAssignTargetPhoto(null);
        setAssignProductId('');
        await fetchGallery();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al asignar la imagen al producto.';
      setErrorMessage(msg);
    } finally {
      setAssigning(false);
    }
  };

  // Cambio rápido de rol en una foto ya asignada
  const handleQuickRoleChange = async (photo, targetRole) => {
    if (!photo.assigned_to?.id) return;
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const response = await axiosInstance.post('/admin/catalog/gallery/assign', {
        image_url: photo.originalSrc || photo.src,
        product_id: photo.assigned_to.id,
        rol: targetRole
      });
      if (response.data.success) {
        setSuccessMessage(response.data.message);
        await fetchGallery();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al cambiar rol de la imagen.';
      setErrorMessage(msg);
    }
  };

  // Desasignar imagen de producto (dejarla libre)
  const handleUnassign = async (photo) => {
    if (!photo.assigned_to?.id) return;
    if (!window.confirm(`¿Deseas desasignar esta imagen del producto "${photo.assigned_to.nombre}"? La imagen quedará libre en la galería.`)) return;
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const response = await axiosInstance.post('/admin/catalog/gallery/assign', {
        image_url: photo.originalSrc || photo.src,
        product_id: photo.assigned_to.id,
        rol: 'unassign'
      });
      if (response.data.success) {
        setSuccessMessage(response.data.message);
        await fetchGallery();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al desasignar la imagen.';
      setErrorMessage(msg);
    }
  };

  const openLightBox = (idx) => {
    setIndex(idx);
    setIsOpen(true);
  };

  // Métricas y conteos dinámicos
  const totalCount = photos.length;
  const b2cCount = photos.filter(p => p.assigned_to?.tipo === 'b2c').length;
  const b2bCount = photos.filter(p => p.assigned_to?.tipo === 'b2b').length;
  const reservaCount = photos.filter(p => p.assigned_to?.tipo === 'reserva' || p.assigned_to?.tipo === 'galeria').length;
  const unassignedCount = photos.filter(p => !p.is_in_use && !p.assigned_to).length;

  // Filtrado de fotos
  const filteredPhotos = photos.filter(p => {
    const fileName = (p.filename || p.originalSrc || p.src || '').toLowerCase();
    const assignedSku = (p.assigned_to?.sku || '').toLowerCase();
    const assignedName = (p.assigned_to?.nombre || '').toLowerCase();
    const query = search.toLowerCase();

    // 1. Búsqueda de texto
    if (search && !fileName.includes(query) && !assignedSku.includes(query) && !assignedName.includes(query)) {
      return false;
    }

    // 2. Filtro por Rol
    if (roleFilter === 'b2c' && p.assigned_to?.tipo !== 'b2c') return false;
    if (roleFilter === 'b2b' && p.assigned_to?.tipo !== 'b2b') return false;
    if (roleFilter === 'reserva' && p.assigned_to?.tipo !== 'reserva' && p.assigned_to?.tipo !== 'galeria') return false;
    if (roleFilter === 'unassigned' && (p.is_in_use || p.assigned_to)) return false;
    if (roleFilter === 'assigned' && !p.is_in_use && !p.assigned_to) return false;

    // 3. Filtro por Producto
    if (productFilter && p.assigned_to?.id !== productFilter && p.assigned_to?.sku !== productFilter) {
      return false;
    }

    return true;
  });

  const selectedProductObj = products.find(prod => prod.id === productFilter || prod.sku === productFilter);

  // Lista de productos para el modal de asignación (con filtro de texto interno)
  const filteredProductsModal = products.filter(prod => {
    if (!productSearchModal) return true;
    const q = productSearchModal.toLowerCase();
    return (prod.sku || '').toLowerCase().includes(q) || (prod.nombre || '').toLowerCase().includes(q);
  });

  return (
    <>
      {/* Page Header */}
      <div className="page-header d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h3 className="page-title mb-1" style={{ color: '#00024f', fontWeight: 'bold' }}>
            <i className="mdi mdi-image-multiple text-primary me-2"></i>
            {t('gallery_title', 'Gestor de Galería Multimedia')}
          </h3>
          <p className="text-muted small mb-0">
            {t('gallery_subtitle', 'Control central de fotos B2C, B2B y Fotos en Reserva ("En la Banca") para el catálogo oficial.')}
          </p>
        </div>
        <div>
          <input 
            type="file" 
            multiple 
            accept="image/png, image/jpeg, image/webp" 
            style={{ display: 'none' }} 
            ref={fileInputRef} 
            onChange={handleFileUpload} 
          />
          <Button 
            style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px', padding: '10px 22px', fontWeight: 'bold' }} 
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading || loading}
            className="shadow-sm"
          >
            {uploading ? (
              <><Spinner size="sm" animation="border" className="me-2" /> {t('uploading_images', 'Subiendo imágenes...')}</>
            ) : (
              <><i className="mdi mdi-cloud-upload me-2"></i> {t('btn_upload_image', 'Subir Nuevas Imágenes')}</>
            )}
          </Button>
        </div>
      </div>

      {/* Notifications / Alerts */}
      {errorMessage && (
        <Alert 
          variant="danger" 
          dismissible 
          onClose={() => setErrorMessage('')} 
          className="shadow-sm mb-4" 
          style={{ borderRadius: '10px', borderLeft: '5px solid #dc2626' }}
        >
          <div className="d-flex align-items-center">
            <i className="mdi mdi-alert-circle fs-4 me-2"></i>
            <div>{errorMessage}</div>
          </div>
        </Alert>
      )}

      {successMessage && (
        <Alert 
          variant="success" 
          dismissible 
          onClose={() => setSuccessMessage('')} 
          className="shadow-sm mb-4" 
          style={{ borderRadius: '10px', borderLeft: '5px solid #10b981' }}
        >
          <div className="d-flex align-items-center">
            <i className="mdi mdi-check-circle fs-4 me-2"></i>
            <div>{successMessage}</div>
          </div>
        </Alert>
      )}

      {/* Tarjetas de Métricas y Filtros Rápidos */}
      <div className="row g-2 mb-4">
        <div className="col-6 col-md">
          <div 
            className="p-3 bg-white rounded border shadow-sm cursor-pointer h-100"
            style={{ 
              borderColor: roleFilter === 'all' && !productFilter ? '#183eeb' : '#e2e8f0',
              borderWidth: roleFilter === 'all' && !productFilter ? '2px' : '1px',
              transition: 'all 0.2s ease'
            }}
            onClick={() => { setRoleFilter('all'); setProductFilter(''); }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-muted fw-semibold">Todas las Fotos</span>
              <i className="mdi mdi-image-multiple text-primary fs-5"></i>
            </div>
            <h4 className="fw-bold mb-0 mt-2 text-dark">{totalCount}</h4>
          </div>
        </div>

        <div className="col-6 col-md">
          <div 
            className="p-3 bg-white rounded border shadow-sm cursor-pointer h-100"
            style={{ 
              borderColor: roleFilter === 'b2c' ? '#0284c7' : '#e2e8f0',
              borderWidth: roleFilter === 'b2c' ? '2px' : '1px',
              transition: 'all 0.2s ease'
            }}
            onClick={() => { setRoleFilter('b2c'); }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-info fw-semibold">Principal B2C</span>
              <i className="mdi mdi-shopping text-info fs-5"></i>
            </div>
            <h4 className="fw-bold mb-0 mt-2 text-dark">{b2cCount}</h4>
          </div>
        </div>

        <div className="col-6 col-md">
          <div 
            className="p-3 bg-white rounded border shadow-sm cursor-pointer h-100"
            style={{ 
              borderColor: roleFilter === 'b2b' ? '#183eeb' : '#e2e8f0',
              borderWidth: roleFilter === 'b2b' ? '2px' : '1px',
              transition: 'all 0.2s ease'
            }}
            onClick={() => { setRoleFilter('b2b'); }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-primary fw-semibold">Principal B2B</span>
              <i className="mdi mdi-package-variant-closed text-primary fs-5"></i>
            </div>
            <h4 className="fw-bold mb-0 mt-2 text-dark">{b2bCount}</h4>
          </div>
        </div>

        <div className="col-6 col-md">
          <div 
            className="p-3 bg-white rounded border shadow-sm cursor-pointer h-100"
            style={{ 
              borderColor: roleFilter === 'reserva' ? '#00024f' : '#e2e8f0',
              borderWidth: roleFilter === 'reserva' ? '2px' : '1px',
              transition: 'all 0.2s ease'
            }}
            onClick={() => { setRoleFilter('reserva'); }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-dark fw-semibold">En Reserva</span>
              <i className="mdi mdi-image-album text-dark fs-5"></i>
            </div>
            <h4 className="fw-bold mb-0 mt-2 text-dark">{reservaCount}</h4>
          </div>
        </div>

        <div className="col-6 col-md">
          <div 
            className="p-3 bg-white rounded border shadow-sm cursor-pointer h-100"
            style={{ 
              borderColor: roleFilter === 'unassigned' ? '#10b981' : '#e2e8f0',
              borderWidth: roleFilter === 'unassigned' ? '2px' : '1px',
              transition: 'all 0.2s ease'
            }}
            onClick={() => { setRoleFilter('unassigned'); }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-success fw-semibold">Disponibles</span>
              <i className="mdi mdi-check-circle text-success fs-5"></i>
            </div>
            <h4 className="fw-bold mb-0 mt-2 text-dark">{unassignedCount}</h4>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="row grid-margin">
        <div className="col-lg-12">
          <Card className="shadow-sm border-0" style={{ borderRadius: '14px', backgroundColor: '#ffffff' }}>
            <Card.Body className="p-4">
              
              {/* Barra de Filtros y Búsqueda */}
              <div className="row g-2 align-items-center mb-4 pb-3 border-bottom">
                {/* 1. Buscador de texto */}
                <div className="col-md-4">
                  <div className="position-relative">
                    <i 
                      className="mdi mdi-magnify position-absolute text-muted" 
                      style={{ left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '1.15rem', zIndex: 5, pointerEvents: 'none' }}
                    />
                    <Form.Control
                      type="text"
                      placeholder={t('search_gallery', 'Buscar por archivo, SKU o nombre de producto...')}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="w-100 shadow-none"
                      style={{
                        paddingLeft: '40px',
                        paddingRight: '12px',
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        height: '44px',
                        lineHeight: '1.4',
                        marginTop: 0,
                        marginBottom: 0,
                        borderRadius: '10px',
                        border: isDark ? '1.5px solid #334155' : '1.5px solid #cbd5e1',
                        backgroundColor: isDark ? '#161e38' : '#f8fafc',
                        color: isDark ? '#ffffff' : '#010028',
                        fontSize: '0.88rem'
                      }}
                    />
                  </div>
                </div>

                {/* 2. Filtro por Rol */}
                <div className="col-md-4">
                  <Form.Select
                    value={roleFilter}
                    onChange={e => setRoleFilter(e.target.value)}
                    className="shadow-none"
                    style={{
                      height: '44px',
                      lineHeight: '1.4',
                      paddingTop: '6px',
                      paddingBottom: '6px',
                      paddingLeft: '14px',
                      paddingRight: '36px',
                      marginTop: 0,
                      marginBottom: 0,
                      borderRadius: '10px',
                      border: isDark ? '1.5px solid #334155' : '1.5px solid #cbd5e1',
                      backgroundColor: isDark ? '#161e38' : '#f8fafc',
                      color: isDark ? '#ffffff' : '#010028',
                      fontSize: '0.86rem',
                      fontWeight: '500'
                    }}
                  >
                    <option value="all">Todos los roles</option>
                    <option value="b2c">Principal B2C (Empaque / Detal)</option>
                    <option value="b2b">Principal B2B (Caja / Mayorista)</option>
                    <option value="reserva">En Reserva (Banca)</option>
                    <option value="unassigned">Disponibles (Sin asignar)</option>
                    <option value="assigned">Asignadas a productos</option>
                  </Form.Select>
                </div>

                {/* 3. Filtro por Producto / SKU */}
                <div className="col-md-4">
                  <Form.Select
                    value={productFilter}
                    onChange={e => setProductFilter(e.target.value)}
                    className="shadow-none"
                    style={{
                      height: '44px',
                      lineHeight: '1.4',
                      paddingTop: '6px',
                      paddingBottom: '6px',
                      paddingLeft: '14px',
                      paddingRight: '36px',
                      marginTop: 0,
                      marginBottom: 0,
                      borderRadius: '10px',
                      border: isDark ? '1.5px solid #334155' : '1.5px solid #cbd5e1',
                      backgroundColor: isDark ? '#161e38' : '#f8fafc',
                      color: isDark ? '#ffffff' : '#010028',
                      fontSize: '0.86rem',
                      fontWeight: '500'
                    }}
                  >
                    <option value="">Todos los productos</option>
                    {products.map(prod => (
                      <option key={prod.id} value={prod.id}>
                        {prod.sku} - {prod.nombre}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              {/* Banner informativo de auditoría por producto */}
              {selectedProductObj && (
                <div 
                  className="p-3 mb-4 rounded border d-flex flex-wrap justify-content-between align-items-center gap-2" 
                  style={{ 
                    backgroundColor: isDark ? '#101d3f' : '#eff6ff', 
                    borderColor: isDark ? '#1d4ed8' : '#bfdbfe' 
                  }}
                >
                  <div>
                    <span className="badge bg-primary me-2" style={{ backgroundColor: '#183eeb' }}>SKU: {selectedProductObj.sku}</span>
                    <strong style={{ color: isDark ? '#ffffff' : '#010028' }}>{selectedProductObj.nombre}</strong>
                    <div className="small text-muted mt-1">
                      Auditoría de imágenes de este producto: {filteredPhotos.length} fotos en total en el sistema.
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline-primary" 
                    style={{ borderRadius: '8px', fontSize: '0.78rem' }}
                    onClick={() => setProductFilter('')}
                  >
                    <i className="mdi mdi-close me-1"></i> Quitar filtro de producto
                  </Button>
                </div>
              )}

              {/* Dynamic Loading State */}
              {loading ? (
                <LoadingState
                  size="lg"
                  title={t('loading_gallery', 'Cargando galería multimedia...')}
                  description={t('loading_gallery_desc', 'Consultando imágenes del almacenamiento central Rutex')}
                />
              ) : filteredPhotos.length === 0 ? (
                <div className="text-center py-5 my-4">
                  <div className="mb-3 d-inline-flex p-4 rounded-circle" style={{ backgroundColor: '#f1f5f9' }}>
                    <i className="mdi mdi-image-off" style={{ fontSize: '48px', color: '#94a3b8' }}></i>
                  </div>
                  <h5 className="fw-bold text-dark mb-1">{t('no_images_found', 'No se encontraron imágenes en la galería.')}</h5>
                  <p className="text-muted small mb-3">
                    {search || roleFilter !== 'all' || productFilter 
                      ? 'Ninguna imagen coincide con los filtros seleccionados.' 
                      : t('no_images_in_server', 'Aún no hay imágenes en el servidor central.')}
                  </p>
                  <Button 
                    variant="outline-primary"
                    style={{ borderRadius: '8px', fontWeight: 'bold' }}
                    onClick={() => { setSearch(''); setRoleFilter('all'); setProductFilter(''); }}
                  >
                    <i className="mdi mdi-refresh me-1"></i> Restablecer filtros
                  </Button>
                </div>
              ) : (
                /* Uniform Responsive Card Grid */
                <Row className="g-3">
                  {filteredPhotos.map((photo, idx) => {
                    const fileName = photo.filename || (photo.originalSrc ? photo.originalSrc.split('/').pop() : photo.src.split('/').pop());
                    const isDeleting = deletingSrc === photo.originalSrc;
                    const assigned = photo.assigned_to;
                    const isB2c = assigned?.tipo === 'b2c';
                    const isB2b = assigned?.tipo === 'b2b';
                    const isReserva = assigned?.tipo === 'reserva' || assigned?.tipo === 'galeria';
                    const isAvailable = !photo.is_in_use && !assigned;

                    return (
                      <Col xs={12} sm={6} md={4} lg={3} xl={3} key={idx}>
                        <Card 
                          className="h-100 shadow-sm border"
                          style={{ 
                            borderRadius: '12px',
                            overflow: 'hidden',
                            backgroundColor: isDark ? '#161e38' : '#ffffff',
                            borderColor: isB2c ? '#38bdf8' : (isB2b ? '#818cf8' : (isReserva ? (isDark ? '#334155' : '#cbd5e1') : (isDark ? '#233160' : '#e2e8f0'))),
                            borderWidth: isB2c || isB2b ? '1.5px' : '1px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {/* Image Box */}
                          <div 
                            style={{ 
                              height: '150px', 
                              backgroundColor: isDark ? '#0b1329' : '#ffffff', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              padding: '10px', 
                              position: 'relative', 
                              borderBottom: isDark ? '1px solid #233160' : '1px solid #f1f5f9',
                              cursor: 'pointer'
                            }}
                            onClick={() => openLightBox(idx)}
                          >
                            <img 
                              src={photo.src} 
                              alt={fileName} 
                              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                            />
                            
                            {/* Badges de Estado / Rol */}
                            <div className="position-absolute top-0 start-0 m-2 d-flex flex-column gap-1">
                              {isB2c && (
                                <Badge bg="info" className="shadow-sm" style={{ borderRadius: '6px', fontSize: '0.68rem' }}>
                                  <i className="mdi mdi-shopping me-1"></i> Principal B2C
                                </Badge>
                              )}
                              {isB2b && (
                                <Badge bg="primary" className="shadow-sm" style={{ backgroundColor: '#183eeb', borderRadius: '6px', fontSize: '0.68rem' }}>
                                  <i className="mdi mdi-package-variant-closed me-1"></i> Principal B2B
                                </Badge>
                              )}
                              {isReserva && (
                                <Badge bg="dark" className="shadow-sm" style={{ backgroundColor: '#00024f', borderRadius: '6px', fontSize: '0.68rem' }}>
                                  <i className="mdi mdi-image-album me-1"></i> En Reserva
                                </Badge>
                              )}
                              {isAvailable && (
                                <Badge bg="success" className="shadow-sm" style={{ borderRadius: '6px', fontSize: '0.68rem' }}>
                                  <i className="mdi mdi-check me-1"></i> Disponible
                                </Badge>
                              )}
                            </div>

                            {/* Action Buttons Overlay (Zoom & Delete) */}
                            <div className="position-absolute top-0 end-0 m-2 d-flex gap-1" onClick={e => e.stopPropagation()}>
                              <Button 
                                size="sm" 
                                variant="light" 
                                style={{ borderRadius: '50%', width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} 
                                onClick={() => openLightBox(idx)}
                                title="Ver en tamaño completo"
                              >
                                <i className="mdi mdi-magnify-plus-outline" style={{ fontSize: '13px' }}></i>
                              </Button>

                              <Button 
                                size="sm" 
                                variant="outline-danger" 
                                style={{ borderRadius: '50%', width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} 
                                onClick={(e) => handleDelete(photo.originalSrc, e)}
                                disabled={isDeleting}
                                title={assigned ? `En uso por ${assigned.sku}. Solo se puede eliminar si se desasigna primero.` : t('delete_from_server', 'Eliminar del Servidor')}
                              >
                                {isDeleting ? <Spinner size="sm" animation="border" style={{ width: '12px', height: '12px' }} /> : <i className="mdi mdi-delete" style={{ fontSize: '13px' }}></i>}
                              </Button>
                            </div>
                          </div>

                          {/* Footer with file title & Product Assignment Details */}
                          <Card.Footer className="p-2 border-0 d-flex flex-column justify-content-between flex-grow-1" style={{ backgroundColor: isDark ? '#161e38' : '#ffffff' }}>
                            <div className="mb-2">
                              <small className="text-truncate d-block fw-semibold mb-1" title={fileName} style={{ fontSize: '0.78rem', color: isDark ? '#ffffff' : '#010028' }}>
                                {fileName}
                              </small>

                              {assigned ? (
                                <div 
                                  className="p-1 rounded border text-start" 
                                  style={{ 
                                    fontSize: '0.72rem',
                                    backgroundColor: isDark ? '#0b1329' : '#f8fafc',
                                    borderColor: isDark ? '#233160' : '#e2e8f0'
                                  }}
                                >
                                  <div className="d-flex align-items-center justify-content-between mb-1">
                                    <span className="fw-bold text-primary">{assigned.sku}</span>
                                    <button 
                                      className="btn btn-sm btn-link text-muted p-0 text-decoration-none" 
                                      style={{ fontSize: '0.65rem' }}
                                      onClick={() => setProductFilter(assigned.id)}
                                      title="Filtrar todas las fotos de este producto"
                                    >
                                      <i className="mdi mdi-filter me-1"></i>Ver producto
                                    </button>
                                  </div>
                                  <div className="text-truncate" title={assigned.nombre} style={{ fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                    {assigned.nombre}
                                  </div>
                                </div>
                              ) : (
                                <div 
                                  className="p-1 rounded border text-center text-muted" 
                                  style={{ 
                                    fontSize: '0.72rem',
                                    backgroundColor: isDark ? '#0b1329' : '#f8fafc',
                                    borderColor: isDark ? '#233160' : '#e2e8f0'
                                  }}
                                >
                                  <span className="text-success fw-semibold">Sin asignar a producto</span>
                                </div>
                              )}
                            </div>

                            {/* Botones de Acción y Gestión de Roles */}
                            <div>
                              {assigned ? (
                                <div className="d-flex gap-1 justify-content-between align-items-center pt-1" style={{ borderTop: isDark ? '1px solid #233160' : '1px solid #e2e8f0' }}>
                                  <div className="d-flex gap-1">
                                    {!isB2c && (
                                      <Button
                                        size="sm"
                                        style={{ backgroundColor: '#0284c7', borderColor: '#0284c7', color: 'white', borderRadius: '4px', fontSize: '0.64rem', padding: '2px 5px' }}
                                        onClick={() => handleQuickRoleChange(photo, 'b2c')}
                                        title="Promover a Principal B2C"
                                      >
                                        Hacer B2C
                                      </Button>
                                    )}
                                    {!isB2b && (
                                      <Button
                                        size="sm"
                                        style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '4px', fontSize: '0.64rem', padding: '2px 5px' }}
                                        onClick={() => handleQuickRoleChange(photo, 'b2b')}
                                        title="Promover a Principal B2B"
                                      >
                                        Hacer B2B
                                      </Button>
                                    )}
                                    {!isReserva && (
                                      <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        style={{ borderRadius: '4px', fontSize: '0.64rem', padding: '2px 5px' }}
                                        onClick={() => handleQuickRoleChange(photo, 'reserva')}
                                        title="Mover a la banca de Reserva"
                                      >
                                        A Reserva
                                      </Button>
                                    )}
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    style={{ borderRadius: '4px', fontSize: '0.64rem', padding: '2px 5px' }}
                                    onClick={() => handleUnassign(photo)}
                                    title="Desasignar del producto (dejar libre)"
                                  >
                                    Liberar
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  className="w-100"
                                  style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '6px', fontSize: '0.72rem', padding: '3px 8px', fontWeight: 'bold' }}
                                  onClick={() => handleOpenAssignModal(photo)}
                                >
                                  <i className="mdi mdi-link-variant me-1"></i> Asignar a Producto
                                </Button>
                              )}
                            </div>
                          </Card.Footer>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Modal de Asignación de Imagen a Producto */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered>
        <Form onSubmit={handleAssignSubmit}>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
            <Modal.Title className="h5 mb-0 fw-bold d-flex align-items-center">
              <i className="mdi mdi-image-plus me-2 text-primary"></i>
              Asignar Imagen a Producto
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4" style={{ backgroundColor: isDark ? '#11192e' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
            {assignTargetPhoto && (
              <div 
                className="d-flex align-items-center gap-3 p-2 mb-3 rounded border"
                style={{
                  backgroundColor: isDark ? '#0b1329' : '#f8fafc',
                  borderColor: isDark ? '#233160' : '#e2e8f0'
                }}
              >
                <img 
                  src={assignTargetPhoto.src} 
                  alt="Target preview" 
                  style={{ width: '60px', height: '60px', objectFit: 'contain', backgroundColor: isDark ? '#11192e' : '#fff', borderRadius: '6px', border: isDark ? '1px solid #233160' : '1px solid #cbd5e1' }}
                />
                <div className="text-truncate flex-grow-1">
                  <small className="fw-bold d-block text-truncate" style={{ fontSize: '0.8rem', color: isDark ? '#ffffff' : '#010028' }}>
                    {assignTargetPhoto.filename || assignTargetPhoto.src.split('/').pop()}
                  </small>
                  <small style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Selecciona el producto y el rol en el que deseas activarla.
                  </small>
                </div>
              </div>
            )}

            {/* Selector de Producto */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small mb-1" style={{ color: isDark ? '#ffffff' : '#010028' }}>Producto Destino</Form.Label>
              <Form.Control
                type="text"
                placeholder="Escribe para buscar por SKU o nombre..."
                value={productSearchModal}
                onChange={e => setProductSearchModal(e.target.value)}
                className="mb-2 shadow-none"
                style={{ 
                  fontSize: '0.82rem',
                  backgroundColor: isDark ? '#161e38' : '#ffffff',
                  borderColor: isDark ? '#334155' : '#cbd5e1',
                  color: isDark ? '#ffffff' : '#010028'
                }}
              />
              <Form.Select
                required
                value={assignProductId}
                onChange={e => setAssignProductId(e.target.value)}
                style={{ 
                  borderRadius: '8px', 
                  fontSize: '0.84rem',
                  backgroundColor: isDark ? '#161e38' : '#ffffff',
                  borderColor: isDark ? '#334155' : '#cbd5e1',
                  color: isDark ? '#ffffff' : '#010028'
                }}
              >
                <option value="">Selecciona un producto...</option>
                {filteredProductsModal.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.sku} - {p.nombre}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Selector de Rol */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small mb-1" style={{ color: isDark ? '#ffffff' : '#010028' }}>Rol de la Imagen en el Producto</Form.Label>
              <div className="d-flex flex-column gap-2">
                <div 
                  className={`p-2 rounded border cursor-pointer ${assignRole === 'b2c' ? 'border-info' : ''}`}
                  style={{
                    backgroundColor: assignRole === 'b2c' ? (isDark ? '#10243d' : '#f0f9ff') : (isDark ? '#161e38' : '#ffffff'),
                    borderColor: assignRole === 'b2c' ? '#0284c7' : (isDark ? '#233160' : '#e2e8f0')
                  }}
                  onClick={() => setAssignRole('b2c')}
                >
                  <Form.Check
                    type="radio"
                    id="role-b2c"
                    name="assignRole"
                    label={<span className="fw-semibold" style={{ fontSize: '0.82rem', color: isDark ? '#ffffff' : '#010028' }}><i className="mdi mdi-shopping text-info me-1"></i> Principal B2C (Empaque / Detal)</span>}
                    checked={assignRole === 'b2c'}
                    onChange={() => setAssignRole('b2c')}
                  />
                  <small className="d-block ms-4" style={{ fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Visible en la app para compradores retail, bodegones y autoservicios.
                  </small>
                </div>

                <div 
                  className={`p-2 rounded border cursor-pointer ${assignRole === 'b2b' ? 'border-primary' : ''}`}
                  style={{
                    backgroundColor: assignRole === 'b2b' ? (isDark ? '#141d3d' : '#eff6ff') : (isDark ? '#161e38' : '#ffffff'),
                    borderColor: assignRole === 'b2b' ? '#183eeb' : (isDark ? '#233160' : '#e2e8f0')
                  }}
                  onClick={() => setAssignRole('b2b')}
                >
                  <Form.Check
                    type="radio"
                    id="role-b2b"
                    name="assignRole"
                    label={<span className="fw-semibold" style={{ fontSize: '0.82rem', color: isDark ? '#ffffff' : '#010028' }}><i className="mdi mdi-package-variant-closed text-primary me-1"></i> Principal B2B (Caja / Mayorista)</span>}
                    checked={assignRole === 'b2b'}
                    onChange={() => setAssignRole('b2b')}
                  />
                  <small className="d-block ms-4" style={{ fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Visible en la app para transformadores, panaderías e industriales al mayor.
                  </small>
                </div>

                <div 
                  className={`p-2 rounded border cursor-pointer ${assignRole === 'reserva' ? 'border-secondary' : ''}`}
                  style={{
                    backgroundColor: assignRole === 'reserva' ? (isDark ? '#1e2433' : '#f8fafc') : (isDark ? '#161e38' : '#ffffff'),
                    borderColor: assignRole === 'reserva' ? (isDark ? '#94a3b8' : '#475569') : (isDark ? '#233160' : '#e2e8f0')
                  }}
                  onClick={() => setAssignRole('reserva')}
                >
                  <Form.Check
                    type="radio"
                    id="role-reserva"
                    name="assignRole"
                    label={<span className="fw-semibold" style={{ fontSize: '0.82rem', color: isDark ? '#ffffff' : '#010028' }}><i className="mdi mdi-image-album text-warning me-1"></i> En Reserva ("En la Banca")</span>}
                    checked={assignRole === 'reserva'}
                    onChange={() => setAssignRole('reserva')}
                  />
                  <small className="d-block ms-4" style={{ fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Queda guardada en el catálogo para ser promovida a principal en cualquier momento.
                  </small>
                </div>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: isDark ? '#11192e' : '#f8f9fa', borderTop: isDark ? '1px solid #233160' : '1px solid #dee2e6' }}>
            <Button variant="secondary" size="sm" onClick={() => setShowAssignModal(false)} style={{ borderRadius: '8px' }}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              size="sm" 
              disabled={assigning || !assignProductId} 
              style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '8px', fontWeight: 'bold' }}
            >
              {assigning ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="mdi mdi-check me-1"></i>}
              Confirmar Asignación
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Lightbox Modal */}
      <Lightbox 
        open={isOpen}
        close={() => setIsOpen(false)}
        slides={filteredPhotos.map(image => ({ src: image.src }))}
        index={index}
        plugins={[Fullscreen, Slideshow, Thumbnails, Zoom]}
      />
    </>
  );
}

export default Gallery;